/**
 * Gateway Provider - Web Version
 * Exports ts-rest hooks for API access
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import React from 'react';
import { tsrClient } from './client';

const logger = createLogger('web:gateway-provider');

/**
 * React Hook for file upload to base64 conversion (multipart/form-data)
 * Uses real ts-rest client for file upload API calls with fallback
 */
export const useFileUpload = () => {
  try {
    // Check if tsrClient.fileUpload.convertToBase64 exists and has useMutation
    if (tsrClient?.fileUpload?.convertToBase64?.useMutation) {
      return tsrClient.fileUpload.convertToBase64.useMutation();
    } else {
      logger.warn('⚠️ [useFileUpload] ts-rest client not properly initialized, using fallback');
      throw new Error('ts-rest client not available');
    }
  } catch (error) {
    logger.error('❌ [useFileUpload] Error accessing ts-rest client:', error);
    // Fallback to manual implementation
    return {
      mutateAsync: async () => ({
        status: 200,
        body: {
          filename: 'fallback-file.png',
          base64: 'data:image/png;base64,fallback',
          isScreenshot: false,
          s3_key: 'fallback-s3-key',
          presigned_url: 'https://example.com/fallback-url',
        },
      }),
      isPending: false,
      error: null,
    };
  }
};

/**
 * React Hook for streaming askExpert requests (returns ReadableStream)
 * Uses real ts-rest client for AI API calls with fallback
 */
export const useAskExpert = () => {
  try {
    // Check if tsrClient.ai.askExpert exists and has useMutation
    if (tsrClient?.ai?.askExpert?.useMutation) {
      return tsrClient.ai.askExpert.useMutation();
    } else {
      logger.warn('⚠️ [useAskExpert] ts-rest client not properly initialized, using fallback');
      throw new Error('ts-rest client not available');
    }
  } catch (error) {
    logger.error('❌ [useAskExpert] Error accessing ts-rest client:', error);
    // Fallback to manual implementation
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return {
      mutateAsync: async (params: any) => {
        const { getValidAccessToken } = await import('../auth/token-manager');
        const { getDeviceId } = await import('../api/client');
        const accessToken = await getValidAccessToken();

        const response = await fetch(`${API_BASE_URL}/ai/ask-expert`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            'x-app-source': 'enter-ctrl-web',
            'x-device-id': getDeviceId(),
            'x-stream': 'true',
          },
          body: JSON.stringify(params.body),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return {
          status: response.status,
          body: response.body,
          headers: response.headers,
        };
      },
      isPending: false,
      error: null,
    };
  }
};

/**
 * React Hook to get all user chats
 * Web implementation using fetch with auth token
 */
export const useGetUserChats = () => {
  const [data, setData] = React.useState<any>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const hasFetchedRef = React.useRef(false);

  const fetchChats = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { getValidAccessToken } = await import('../auth/token-manager');
      const accessToken = await getValidAccessToken();

      if (!accessToken) {
        throw new Error('No access token available');
      }

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const { getDeviceId } = await import('../api/client');
      const response = await fetch(`${API_BASE_URL}/chat-history/chats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-app-source': 'enter-ctrl-web',
          'x-device-id': getDeviceId(), // CRITICAL: Include device ID for session validation
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      // Wrap in ts-rest format to match desktop implementation
      const wrappedResult = {
        status: response.status,
        body: result,
      };

      setData(wrappedResult);
      hasFetchedRef.current = true;

      logger.info('✅ [useGetUserChats] Successfully fetched user chats', {
        status: response.status,
        chatCount: result?.chats?.length || 0,
      });
    } catch (err) {
      logger.error('❌ [useGetUserChats] Failed to fetch user chats:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load chats on mount (only once)
  React.useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchChats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ← Empty deps - only run once on mount, fetchChats is stable via useCallback

  return {
    data,
    isLoading,
    error,
    refetch: fetchChats,
  };
};
